import React, { useEffect, useRef, useState } from "react";
import { X, Download, Disc3, ShieldCheck } from "lucide-react";

/**
 * "Burn to CD-R" Mixtape generator. Draws the user's queue onto a canvas
 * styled like a 2003 hand-labeled burned CD-R, plus a tracklist. Offers a
 * PNG download so the user can share their retro mixtape on Twitter/Discord.
 *
 * The first track's artwork is loaded (CORS-safe) and used as the banner
 * hero image at the top. If the image fails to load (tainted canvas) we
 * silently fall back to a painted lemon gradient so the PNG download
 * always works.
 */
export default function MixtapeModal({ queue, onClose, handle }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const firstArt = queue[0]?.artwork || "";

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const loadImage = (url) =>
      new Promise((resolve) => {
        if (!url) { resolve(null); return; }
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });

    (async () => {
      const hero = await loadImage(firstArt);
      if (cancelled) return;
      draw(canvas, queue, handle, hero);
      if (!cancelled) setReady(true);
    })();

    return () => { cancelled = true; };
  }, [queue, handle, firstArt]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDownloading(true);
    try {
      // Use toDataURL for best cross-browser + CORS-safe behavior
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `lemonwire-mixtape-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      // Fallback: blob
      try {
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `lemonwire-mixtape-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        }, "image/png");
      } catch {
        /* ignored */
      }
    } finally {
      setTimeout(() => setDownloading(false), 600);
    }
  };

  return (
    <div
      data-testid="mixtape-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="ie-chrome flex flex-col overflow-hidden max-w-[96vw]"
        style={{ width: 820 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="xp-titlebar-lemon h-[28px] flex items-center justify-between px-2 select-none">
          <div className="flex items-center gap-2 text-[13px] font-bold">
            <Disc3 size={13} />
            Burn to CD-R &middot; Mixtape Preview
          </div>
          <button
            data-testid="mixtape-close"
            onClick={onClose}
            className="w-[22px] h-[19px] rounded-sm bg-gradient-to-b from-[#F58D85] via-[#E81123] to-[#8B0A14] text-white flex items-center justify-center hover:brightness-110"
          >
            <X size={11} strokeWidth={4} />
          </button>
        </div>

        <div className="p-3 bg-[#ECE9D8] mixtape-body flex flex-col items-center gap-3 overflow-y-auto xp-scroll" style={{ maxHeight: "80vh" }}>
          <canvas
            ref={canvasRef}
            width={780}
            height={980}
            className="bevel-in bg-white"
            style={{ width: "100%", maxWidth: 780 }}
          />

          <div className="flex items-center justify-between w-full max-w-[780px] gap-3">
            <div className="text-[11px] text-[#555] italic flex items-center gap-1">
              <ShieldCheck size={12} className="text-[#1F8020]" />
              Save this PNG and share your mixtape. All tracks pre-scanned.
            </div>
            <div className="flex gap-2">
              <button
                data-testid="mixtape-cancel"
                onClick={onClose}
                className="bevel-btn px-4 py-1 text-[12px] font-bold"
              >
                Close
              </button>
              <button
                data-testid="mixtape-download"
                onClick={download}
                disabled={!ready || downloading}
                className="bevel-btn-green px-5 py-1 text-[13px] font-bold flex items-center gap-2 disabled:opacity-70"
              >
                <Download size={13} />
                {downloading ? "Saving..." : "Save as PNG"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Canvas drawing
// ============================================================
function draw(canvas, queue, handle, heroImage) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;

  // ==== Paper background ====
  const paper = ctx.createLinearGradient(0, 0, 0, H);
  paper.addColorStop(0, "#FFFDF3");
  paper.addColorStop(1, "#F0E9C8");
  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, W, H);

  // ==== Hero banner with first-track art ====
  const bannerY = 24;
  const bannerH = 150;
  const bannerX = 24;
  const bannerW = W - 48;
  drawHeroBanner(ctx, heroImage, bannerX, bannerY, bannerW, bannerH);

  // Subtle grain overlay (lightweight)
  const grainImg = ctx.getImageData(0, bannerY + bannerH + 10, W, H - (bannerY + bannerH + 10));
  for (let i = 0; i < grainImg.data.length; i += 4) {
    const n = (Math.random() * 14) - 7;
    grainImg.data[i] = clamp255(grainImg.data[i] + n);
    grainImg.data[i + 1] = clamp255(grainImg.data[i + 1] + n);
    grainImg.data[i + 2] = clamp255(grainImg.data[i + 2] + n);
  }
  ctx.putImageData(grainImg, 0, bannerY + bannerH + 10);

  // ==== CD-R ====
  const cx = W / 2;
  const cy = 400;
  const outerR = 170;
  const innerR = 32;

  // Disc base (silvery)
  const discGrd = ctx.createRadialGradient(cx - 30, cy - 30, 10, cx, cy, outerR);
  discGrd.addColorStop(0, "#FFFFFF");
  discGrd.addColorStop(0.6, "#F2F1EE");
  discGrd.addColorStop(1, "#CFC9B0");
  ctx.fillStyle = discGrd;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
  ctx.fill();

  // Rainbow iridescence ring
  for (let i = 0; i < 180; i++) {
    const ang = (i / 180) * Math.PI * 2;
    const hue = (i * 2) % 360;
    ctx.strokeStyle = `hsla(${hue}, 80%, 70%, 0.18)`;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR - 8 - (i % 30), ang, ang + 0.08);
    ctx.stroke();
  }

  // Label area (white) with a small copy of the hero art in the middle
  ctx.fillStyle = "#FFFDE7";
  ctx.beginPath();
  ctx.arc(cx, cy, 104, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#D4D0BF";
  ctx.lineWidth = 1;
  ctx.stroke();

  if (heroImage) {
    // A tiny round thumbnail of the album art near the top of the label
    const thumbR = 44;
    const thumbY = cy - 30;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, thumbY, thumbR, 0, Math.PI * 2);
    ctx.clip();
    const { sx, sy, sw, sh } = cover(heroImage.naturalWidth || heroImage.width, heroImage.naturalHeight || heroImage.height, thumbR * 2, thumbR * 2);
    ctx.drawImage(heroImage, sx, sy, sw, sh, cx - thumbR, thumbY - thumbR, thumbR * 2, thumbR * 2);
    ctx.restore();
    ctx.strokeStyle = "#D4D0BF";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, thumbY, thumbR, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Center hole
  ctx.fillStyle = "#1A1A1A";
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#F0E9C8";
  ctx.beginPath();
  ctx.arc(cx, cy, innerR - 12, 0, Math.PI * 2);
  ctx.fill();

  // Handwritten marker label text below center hole
  ctx.fillStyle = "#0053e1";
  ctx.font = 'bold 18px "Comic Sans MS", "Marker Felt", cursive';
  ctx.textAlign = "center";
  ctx.fillText("LemonWire", cx, cy + 52);
  ctx.fillStyle = "#8B0A14";
  ctx.font = 'bold 15px "Comic Sans MS", "Marker Felt", cursive';
  ctx.fillText("MIXTAPE '26", cx, cy + 70);
  ctx.fillStyle = "#1F8020";
  ctx.font = '12px "Comic Sans MS", "Marker Felt", cursive';
  ctx.fillText(`for: ${handle}`, cx, cy + 86);

  // ==== "CLEAN" stamp ====
  ctx.save();
  ctx.translate(W - 100, bannerY + bannerH + 46);
  ctx.rotate(-0.18);
  ctx.strokeStyle = "#1F8020";
  ctx.fillStyle = "#1F8020";
  ctx.lineWidth = 3;
  ctx.strokeRect(-48, -24, 96, 48);
  ctx.strokeRect(-44, -20, 88, 40);
  ctx.font = 'bold 16px Impact, "Arial Black", sans-serif';
  ctx.textAlign = "center";
  ctx.fillText("CERTIFIED", 0, -4);
  ctx.fillText("MALWARE-FREE", 0, 14);
  ctx.restore();

  // ==== Tracklist ====
  const listY = 620;
  ctx.fillStyle = "#3D2E00";
  ctx.font = 'bold 18px Tahoma';
  ctx.textAlign = "left";
  ctx.fillText("Side A — Tracklist", 60, listY);

  ctx.strokeStyle = "#3D2E00";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, listY + 6);
  ctx.lineTo(W - 60, listY + 6);
  ctx.stroke();

  ctx.font = '14px "Share Tech Mono", "Courier New", monospace';
  ctx.fillStyle = "#222";
  const maxTracks = Math.min(queue.length, 12);
  for (let i = 0; i < maxTracks; i++) {
    const t = queue[i];
    const n = String(i + 1).padStart(2, "0");
    const line = `${n}.  ${t.artist} — ${t.title}`;
    const truncated = line.length > 58 ? line.slice(0, 57) + "…" : line;
    ctx.fillText(truncated, 60, listY + 34 + i * 24);
  }

  if (queue.length > maxTracks) {
    ctx.fillStyle = "#555";
    ctx.font = 'italic 12px Tahoma';
    ctx.fillText(`...and ${queue.length - maxTracks} more on Side B`, 60, listY + 34 + maxTracks * 24);
  }

  if (queue.length === 0) {
    ctx.fillStyle = "#8B0A14";
    ctx.font = 'italic 14px Tahoma';
    ctx.fillText("Queue is empty — add some bangers first!", 60, listY + 34);
  }

  // ==== Footer ====
  ctx.fillStyle = "#3D2E00";
  ctx.font = '12px Tahoma';
  ctx.textAlign = "center";
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  ctx.fillText(`Burned on ${today}  ·  lemonwire.fm  ·  LemonShield™ v4.2 verified`, W / 2, H - 18);
}

// ============================================================
// Banner helper
// ============================================================
function drawHeroBanner(ctx, heroImage, x, y, w, h) {
  // Fallback lemon gradient if no image or CORS failed
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();

  if (heroImage) {
    const nw = heroImage.naturalWidth || heroImage.width;
    const nh = heroImage.naturalHeight || heroImage.height;
    const { sx, sy, sw, sh } = cover(nw, nh, w, h);
    // Slight blur via draw-twice for a softened poster look
    ctx.globalAlpha = 1;
    ctx.drawImage(heroImage, sx, sy, sw, sh, x, y, w, h);
    // Dark overlay for text legibility
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, "rgba(0,0,0,0.35)");
    grad.addColorStop(0.55, "rgba(0,0,0,0.55)");
    grad.addColorStop(1, "rgba(0,0,0,0.75)");
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  } else {
    const g = ctx.createLinearGradient(x, y, x, y + h);
    g.addColorStop(0, "#FFF59D");
    g.addColorStop(1, "#FBC02D");
    ctx.fillStyle = g;
    ctx.fillRect(x, y, w, h);
  }

  // 3px inset CD-case border
  ctx.strokeStyle = heroImage ? "#FFEB3B" : "#9C7800";
  ctx.lineWidth = 3;
  ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
  ctx.restore();

  // ==== Header text on top ====
  ctx.save();
  ctx.textAlign = "center";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  ctx.fillStyle = heroImage ? "#FFEB3B" : "#3D2E00";
  ctx.font = 'bold 38px "Pixelify Sans", Tahoma, sans-serif';
  ctx.fillText("My LemonWire Mixtape", x + w / 2, y + 58);

  ctx.shadowBlur = 4;
  ctx.font = 'italic 15px Tahoma, sans-serif';
  ctx.fillStyle = heroImage ? "#FFFFFF" : "#5A4500";
  ctx.fillText('"No malware. Just bangers."', x + w / 2, y + 86);

  ctx.font = 'bold 12px "Share Tech Mono", "Courier New", monospace';
  ctx.fillStyle = heroImage ? "#B6FF00" : "#1F8020";
  ctx.fillText("♫ · LIMITED RIP · 100% CLEAN · v2.7.32 GOLD · ♫", x + w / 2, y + h - 14);
  ctx.restore();
}

function cover(srcW, srcH, dstW, dstH) {
  if (!srcW || !srcH) return { sx: 0, sy: 0, sw: srcW, sh: srcH };
  const srcRatio = srcW / srcH;
  const dstRatio = dstW / dstH;
  let sx = 0, sy = 0, sw = srcW, sh = srcH;
  if (srcRatio > dstRatio) {
    sw = srcH * dstRatio;
    sx = (srcW - sw) / 2;
  } else {
    sh = srcW / dstRatio;
    sy = (srcH - sh) / 2;
  }
  return { sx, sy, sw, sh };
}

function clamp255(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}
